import React from "react";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "react-datepicker/dist/react-datepicker.css";

const CommonModalForm = ({
  visible,
  title = "Form",
  fields = [],
  onCancel,
  onSubmit,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  values = {}, // <-- Default to empty object
  onChange,
  errors = {},
  requiredFields = [],
  imagePreview = null,
  disabled = false, 
}) => {
  if (!visible) return null;

  const handleFieldChange = (name, value) => {
    onChange({
      ...values,
      [name]: value,
    });
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content w-100" style={{ borderRadius: "16px" }}>
          <div className="modal-header border-bottom">
            <h5 className="modal-title text-muted">{title}</h5>
            <button
              type="button"
              className="btn-close mt-1"
              onClick={onCancel}
            ></button>
          </div>

          <div className="modal-body mt-2 pt-0">
            {fields.map((field, idx) => {
              if (field.type === "heading") {
                return (
                  <div
                    key={`heading-${idx}`}
                    className="mt-3 mb-2 border-bottom pb-4"
                  >
                    <h6 className="fw-semibold text-muted">{field.label}</h6>
                  </div>
                );
              }

              const isRequired = requiredFields.includes(field.name);
              const fieldError = errors[field.name];
              const value = values[field.name] ?? "";

              return (
                <div className="mb-3" key={field.name}>
                  {field.label && (
                    <label className="form-label text-muted">
                      {field.label}{" "}
                      {isRequired && (
                        <span className="text-danger">*</span>
                      )}
                    </label>
                  )}

                  {/* ─────────────── Textarea ─────────────── */}
                  {field.type === "textarea" ? (
                    <textarea
                      className={`form-control ${
                        fieldError ? "is-invalid" : ""
                      }`}
                      placeholder={field.placeholder || ""}
                      value={value}
                      disabled={disabled}
                      onChange={(e) =>
                        handleFieldChange(field.name, e.target.value)
                      }
                      rows={field.rows || 3}
                    />
                  ) : /* ─────────────── Select ─────────────── */
                  field.type === "select" ? (
                    <Select
                      classNamePrefix="select"
                      className={fieldError ? "is-invalid" : ""}
                      options={field.options || []}
                      isMulti={field.isMulti}
                      placeholder={
                        field.placeholder ||
                        `Select ${field.label || field.name}`
                      }
                      value={
                        field.isMulti
                          ? (value || []).map((val) =>
                              field.options.find(
                                (opt) => opt.value === val
                              )
                            )
                          : field.options.find(
                              (opt) => opt.value === value
                            ) || null
                      }
                      onChange={(selected) =>
                        handleFieldChange(
                          field.name,
                          field.isMulti
                            ? (selected || []).map((s) => s.value)
                            : selected?.value || ""
                        )
                      }
                    />
                  ) : /* ─────────────── Radio ─────────────── */
                  field.type === "radio" ? (
                    <div className="d-flex gap-3 flex-wrap">
                      {field.options?.map((opt) => (
                        <label
                          key={opt.value || opt}
                          className="form-check-label"
                        >
                          <input
                            type="radio"
                            name={field.name}
                            value={opt.value || opt}
                            checked={value === (opt.value || opt)}
                            disabled={disabled}
                            onChange={(e) =>
                              handleFieldChange(
                                field.name,
                                e.target.value
                              )
                            }
                            className="form-check-input me-1"
                          />
                          {opt.label || opt}
                        </label>
                      ))}
                    </div>
                  ) : /* ─────────────── Checkbox ─────────────── */
                  field.type === "checkbox" ? (
                    <div className="form-check mt-2">
                      <input
                        type="checkbox"
                        className={`form-check-input ${
                          fieldError ? "is-invalid" : ""
                        }`}
                        id={field.name}
                        checked={!!value}
                        disabled={disabled}
                        style={{ transform: "scale(1.2)" }}
                        onChange={(e) =>
                          handleFieldChange(field.name, e.target.checked)
                        }
                      />
                      <label
                        className="form-check-label ms-2"
                        htmlFor={field.name}
                      >
                        {field.placeholder || field.label}
                      </label>
                    </div>
                  ) : /* ─────────────── Checkbox List ─────────────── */
                  field.type === "checkbox-list" ? (
                    <div className="row">
                      {(field.options || []).length === 0 && (
                        <div className="col-12 text-muted">No options</div>
                      )}
                      {(field.options || []).map((opt) => {
                        const optValue = opt.value ?? opt;
                        const checked = (values[field.name] || []).includes(optValue);
                        return (
                          <div className="col-6 mb-2" key={optValue}>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`${field.name}_${optValue}`}
                                checked={checked}
                                onChange={(e) => {
                                  const current = new Set(values[field.name] || []);
                                  if (e.target.checked) current.add(optValue);
                                  else current.delete(optValue);
                                  handleFieldChange(field.name, Array.from(current));
                                }}
                              />
                              <label className="form-check-label" htmlFor={`${field.name}_${optValue}`}>
                                {opt.label ?? opt}
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : /* ─────────────── Phone ─────────────── */
                  field.type === "phone" ? (
                    <PhoneInput
                      country={"in"}
                      value={value}
                      onChange={(phone) =>
                        handleFieldChange(field.name, phone)
                      }
                      inputProps={{
                        name: field.name,
                        required: isRequired,
                        readOnly: disabled,
                      }}
                      containerStyle={{ width: "100%" }}
                      inputStyle={{
                        width: "100%",
                        borderRadius: "0.375rem",
                        border: "1px solid #d1d5db",
                        padding: "10px 45px 10px 48px",
                      }}
                    />
                  ) : /* ─────────────── File Upload ─────────────── */
                  field.type === "file" ? (
                    <>
                      {(imagePreview && field.name === "images") ||
                      values[field.name] ? (
                        <div className="mb-2">
                          <img
                            src={
                              imagePreview ||
                              (typeof values[field.name] === "string"
                                ? values[field.name]
                                : values[field.name]
                                ? URL.createObjectURL(values[field.name])
                                : "")
                            }
                            alt="Preview"
                            className="img-thumbnail"
                            style={{
                              maxWidth: "200px",
                              height: "auto",
                              maxHeight: "150px",
                            }}
                          />
                          <div className="mt-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-danger me-2"
                              onClick={() =>
                                handleFieldChange(field.name, null)
                              }
                            >
                              Remove Image
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              onClick={() => {
                                const input =
                                  document.createElement("input");
                                input.type = "file";
                                input.accept = field.accept || "image/*";
                                input.onchange = (e) => {
                                  const file = e.target.files[0];
                                  if (file)
                                    handleFieldChange(field.name, file);
                                };
                                input.click();
                              }}
                            >
                              Change Image
                            </button>
                          </div>
                        </div>
                      ) : (
                        <input
                          type="file"
                          className={`form-control ${
                            fieldError ? "is-invalid" : ""
                          }`}
                          accept={field.accept || "image/*"}
                          disabled={disabled}
                          onChange={(e) =>
                            handleFieldChange(
                              field.name,
                              e.target.files[0] || null
                            )
                          }
                        />
                      )}
                    </>
                  ) : /* ─────────────── Default Input ─────────────── */
                  (
                    <input
                      type={field.type || "text"}
                      name={field.name}
                      className={`form-control ${
                        fieldError ? "is-invalid" : ""
                      }`}
                      placeholder={field.placeholder || ""}
                      value={value}
                      disabled={disabled}
                      readOnly={field.readOnly}
                      min={field.min}
                      max={field.max}
                      onChange={(e) =>
                        handleFieldChange(field.name, e.target.value)
                      }
                    />
                  )}

                  {fieldError && (
                    <div className="invalid-feedback d-block">
                      {fieldError}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              style={{ borderRadius: "6px" }}
              onClick={onCancel}
              disabled={disabled}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              style={{ borderRadius: "6px" }}
              onClick={onSubmit}
              disabled={disabled}
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonModalForm;
